import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef, ReactNode } from 'react';
import type { Company } from '../types/database';

/**
 * CompanyContext - Multi-company selection state management
 * 
 * Allows users to select multiple companies simultaneously for:
 * - Consolidated reports (all companies combined)
 * - Individual company views
 * - Cross-company comparisons
 * - Scenarios across multiple entities
 * 
 * @example
 * ```tsx
 * const { selectedCompanies, toggleCompany, selectAll } = useCompanyContext();
 * 
 * // Toggle a company
 * toggleCompany('company-id');
 * 
 * // Select all companies
 * selectAll();
 * ```
 */

interface CompanyContextType {
  // All available companies
  allCompanies: Company[];
  setAllCompanies: (companies: Company[]) => void;
  
  // Selected companies (multi-select)
  selectedCompanies: Company[];
  selectedCompanyIds: string[];
  
  // Selection actions
  toggleCompany: (companyId: string) => void;
  selectCompany: (companyId: string) => void;
  deselectCompany: (companyId: string) => void;
  selectAll: () => void;
  selectNone: () => void;
  setSelectedCompanies: (companies: Company[]) => void;
  
  // Utility
  isSelected: (companyId: string) => boolean;
  hasMultipleSelected: boolean;
  hasSelection: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const STORAGE_KEY = 'cashflow-selected-companies';

interface CompanyProviderProps {
  children: ReactNode;
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [selectedCompanies, setSelectedCompaniesState] = useState<Company[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from localStorage on mount - only once when allCompanies first loads
  useEffect(() => {
    if (allCompanies.length > 0 && !isInitialized) {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const ids = JSON.parse(stored) as string[];
          console.log('[CompanyContext] Loading from localStorage:', {
            storedIds: ids,
            allCompaniesOrder: allCompanies.map(c => ({ id: c.id, name: c.name, currency: c.default_currency }))
          });
          
          if (ids.length > 0) {
            // IMPORTANTE: Manter a ordem dos IDs do localStorage
            const companiesMap = new Map(allCompanies.map(c => [c.id, c]));
            const companies = ids
              .map(id => companiesMap.get(id))
              .filter((c): c is Company => c !== undefined);
            
            console.log('[CompanyContext] Restored from localStorage:', {
              count: companies.length,
              order: companies.map(c => ({ id: c.id, name: c.name, currency: c.default_currency }))
            });
            
            if (companies.length > 0) {
              setSelectedCompaniesState(companies);
              setIsInitialized(true);
              return;
            }
          }
        } catch (error) {
          console.error('Error loading selected companies from localStorage:', error);
        }
      }
      
      // If no stored selection or error, auto-select first company
      setSelectedCompaniesState([allCompanies[0]]);
      setIsInitialized(true);
    }
  }, [allCompanies, isInitialized]);

  // Sync selectedCompanies when allCompanies updates (e.g., after a company is updated)
  // This ensures that selectedCompanies always has the latest data from allCompanies
  useEffect(() => {
    console.log('[CompanyContext] Sync effect triggered:', {
      isInitialized,
      allCompaniesCount: allCompanies.length,
      allCompaniesCurrencies: allCompanies.map(c => ({ name: c.name, currency: c.default_currency }))
    });

    if (isInitialized && allCompanies.length > 0) {
      setSelectedCompaniesState(prevSelected => {
        console.log('[CompanyContext] Inside setState:', {
          prevSelectedCount: prevSelected.length,
          prevSelectedIds: prevSelected.map(c => c.id),
          prevSelectedNames: prevSelected.map(c => c.name),
          prevCurrency: prevSelected[0]?.default_currency,
          prevFirstCompany: prevSelected[0]?.name,
        });

        if (prevSelected.length === 0) return prevSelected;

        // IMPORTANTE: Manter a ordem de seleção do usuário (prevSelected)
        // em vez da ordem do banco (allCompanies)
        const selectedIds = prevSelected.map(c => c.id);
        const companiesMap = new Map(allCompanies.map(c => [c.id, c]));
        const updatedSelectedCompanies = selectedIds
          .map(id => companiesMap.get(id))
          .filter((c): c is Company => c !== undefined);

        console.log('[CompanyContext] Filtered companies:', {
          updatedCount: updatedSelectedCompanies.length,
          updatedIds: updatedSelectedCompanies.map(c => c.id),
          updatedNames: updatedSelectedCompanies.map(c => c.name),
          newCurrency: updatedSelectedCompanies[0]?.default_currency,
          newFirstCompany: updatedSelectedCompanies[0]?.name,
        });

        // ALWAYS update to ensure fresh data (important for currency changes)
        if (updatedSelectedCompanies.length > 0) {
          console.log('[CompanyContext] ✅ Syncing selected companies with updated data', {
            prevCurrency: prevSelected[0]?.default_currency,
            newCurrency: updatedSelectedCompanies[0]?.default_currency,
          });
          return updatedSelectedCompanies;
        }

        return prevSelected;
      });
    }
  }, [allCompanies, isInitialized]);

  // Persist to localStorage
  const setSelectedCompanies = useCallback((companies: Company[]) => {
    setSelectedCompaniesState(companies);
    const ids = companies.map(c => c.id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  }, []);

  // Memoize selectedCompanyIds to prevent infinite loops
  // Keep a stable array reference when the IDs haven't actually changed
  const idsArrayRef = useRef<string[]>([]);
  const selectedCompanyIds = useMemo(() => {
    const newIds = selectedCompanies.map(c => c.id);
    const newKey = newIds.slice().sort().join(',');
    const oldKey = idsArrayRef.current.slice().sort().join(',');
    
    if (newKey !== oldKey) {
      idsArrayRef.current = newIds;
    }
    
    return idsArrayRef.current;
  }, [selectedCompanies]);

  const toggleCompany = useCallback((companyId: string) => {
    setSelectedCompaniesState(prev => {
      const isCurrentlySelected = prev.some(c => c.id === companyId);
      
      if (isCurrentlySelected) {
        // Don't allow deselecting the last company
        if (prev.length === 1) {
          return prev;
        }
        const newSelection = prev.filter(c => c.id !== companyId);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newSelection.map(c => c.id)));
        return newSelection;
      } else {
        const company = allCompanies.find(c => c.id === companyId);
        if (company) {
          const newSelection = [...prev, company];
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newSelection.map(c => c.id)));
          return newSelection;
        }
        return prev;
      }
    });
  }, [allCompanies]);

  const selectCompany = useCallback((companyId: string) => {
    setSelectedCompaniesState(prev => {
      if (!prev.some(c => c.id === companyId)) {
        const company = allCompanies.find(c => c.id === companyId);
        if (company) {
          const newSelection = [...prev, company];
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newSelection.map(c => c.id)));
          return newSelection;
        }
      }
      return prev;
    });
  }, [allCompanies]);

  const deselectCompany = useCallback((companyId: string) => {
    setSelectedCompaniesState(prev => {
      // Don't allow deselecting the last company
      if (prev.length === 1) {
        return prev;
      }
      const newSelection = prev.filter(c => c.id !== companyId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSelection.map(c => c.id)));
      return newSelection;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedCompaniesState(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allCompanies.map(c => c.id)));
      return allCompanies;
    });
  }, [allCompanies]);

  const selectNone = useCallback(() => {
    setSelectedCompaniesState(() => {
      // Keep at least one selected (the first one)
      if (allCompanies.length > 0) {
        const newSelection = [allCompanies[0]];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newSelection.map(c => c.id)));
        return newSelection;
      } else {
        return [];
      }
    });
  }, [allCompanies]);

  const isSelected = useCallback((companyId: string) => {
    return selectedCompanies.some(c => c.id === companyId);
  }, [selectedCompanies]);

  const hasMultipleSelected = selectedCompanies.length > 1;
  const hasSelection = selectedCompanies.length > 0;

  const value: CompanyContextType = useMemo(() => ({
    allCompanies,
    setAllCompanies,
    selectedCompanies,
    selectedCompanyIds,
    toggleCompany,
    selectCompany,
    deselectCompany,
    selectAll,
    selectNone,
    setSelectedCompanies,
    isSelected,
    hasMultipleSelected,
    hasSelection,
  }), [
    allCompanies,
    selectedCompanies,
    selectedCompanyIds,
    toggleCompany,
    selectCompany,
    deselectCompany,
    selectAll,
    selectNone,
    setSelectedCompanies,
    isSelected,
    hasMultipleSelected,
    hasSelection,
  ]);

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompanyContext() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompanyContext must be used within a CompanyProvider');
  }
  return context;
}
